import { z } from "zod";
import { actionSchema } from "./actions";
import { appConfigSchema, widgetBrandingSchema } from "./config";

export const pageContextSchema = z.object({
  path: z.string().max(500),
  title: z.string().max(300).optional(),
});

/**
 * Plafoanele snapshot-ului. Exportate ca valori, nu doar înghițite în schemă:
 * SDK-ul le importă ca să taie ÎNAINTE de trimitere, serverul le aplică la
 * validare — un singur set de cifre pentru ambele capete. Asimetria din builder
 * (SDK 150k vs. server 300k) a fost o sursă de confuzie; aici, deliberat, nu.
 */
export const SNAPSHOT_MAX_TEXT_CHARS = 16000;
export const SNAPSHOT_MAX_ELEMENTS = 200;
export const SNAPSHOT_MAX_ELEMENT_TEXT_CHARS = 80;
export const SNAPSHOT_MAX_HREF_CHARS = 300;

/**
 * Snapshot al paginii vii, capturat de widget la fiecare întrebare: text randat
 * + elementele pe care agentul le poate arăta, indexate prin `idx`.
 *
 * `idx` e un index în snapshot-ul ĂSTA, nu un id stabil: SDK-ul ține harta
 * `idx → nod DOM` în memorie și o reconstruiește la fiecare captură, deci
 * modelul nu mai scrie selectori (nu-i mai poate halucina).
 *
 * NU conține NICIODATĂ `value` din inputuri — nici parole, nici câmpuri
 * completate de vizitator; doar text randat și etichete. E o promisiune publică
 * (privacy §2.2 / DPA Annex I — registru → G12), nu o preferință de design.
 */
export const pageSnapshotElementSchema = z.object({
  idx: z.number().int().min(0),
  tag: z.string().max(20),
  text: z.string().max(SNAPSHOT_MAX_ELEMENT_TEXT_CHARS),
  href: z.string().max(SNAPSHOT_MAX_HREF_CHARS).optional(),
  /**
   * Starea de dezvăluire, doar când situl o declară singur (`aria-expanded`,
   * `role=tab` + `aria-selected`, `<details>`): "collapsed" | "expanded" |
   * "selected" | "not selected". Absentă pe elementele obișnuite — și absentă,
   * deliberat, când situl nu spune nimic (vezi `sdk/src/snapshot.ts`).
   */
  state: z.enum(["collapsed", "expanded", "selected", "not selected"]).optional(),
});

/**
 * Starea coșului pe un app COMMERCE (docs/plan-commerce-actions.md), citită de
 * SDK din API-ul platformei (Shopify: GET /cart.js) și atașată snapshotului.
 * Agentul vede coșul REAL la fiecare tură — „ai deja două în coș" — și observă
 * singur când o adăugare a eșuat. Sume gata formatate, ca la `productCardSchema`.
 *
 * Rămâne în litera promisiunii snapshotului (fără `value` de inputuri):
 * coșul e conținut al paginii — același badge și drawer pe care vizitatorul
 * le vede — nu date tastate în formulare.
 */
export const cartStateSchema = z.object({
  items: z
    .array(
      z.object({
        variantId: z.string().max(64),
        title: z.string().max(300),
        quantity: z.number().int().min(0).max(999),
        linePrice: z.string().max(50).optional(),
      }),
    )
    .max(100),
  itemCount: z.number().int().min(0).max(9999),
  totalPrice: z.string().max(50),
});

export const pageSnapshotSchema = z.object({
  text: z.string().max(SNAPSHOT_MAX_TEXT_CHARS),
  elements: z.array(pageSnapshotElementSchema).max(SNAPSHOT_MAX_ELEMENTS),
  /** Doar pe appuri commerce; absent în rest — un SDK vechi nu-l trimite niciodată. */
  cart: cartStateSchema.optional(),
  /**
   * Ultima acțiune de coș EȘUATĂ de la captura precedentă („add_to_cart:
   * sold out"), fără conținut sensibil. Agentul o vede în tura următoare și
   * corectează verbal — execuția nu blochează niciodată vocea.
   */
  lastActionError: z.string().max(200).optional(),
  /**
   * Câte produse se văd pe pagina de rezultate a temei, numărate structural de
   * SDK (`commerce/shopify.ts` → `searchResults`). Există fiindcă `open_search`
   * predă pagina temei magazinului, iar modelul rămânea să GHICEASCĂ din textul
   * ei dacă lista e goală — pe zero rezultate ghicea prost.
   *
   * Opțional pe toate căile: SDK-urile vechi și siturile care nu sunt magazine
   * nu-l trimit niciodată, iar `count: 0` înseamnă „verificat gol", nu „lipsă
   * de semnal" — SDK-ul preferă să nu trimită nimic decât să trimită un zero
   * pe care nu-l poate susține.
   */
  searchResults: z
    .object({
      query: z.string().max(200).optional(),
      count: z.number().int().min(0).max(200),
    })
    .optional(),
});

/**
 * De ce vine o continuare (valorile de sârmă rămân, semantica lui `navigate` s-a
 * mutat): `navigate` = vizitatorul a urmat linkul evidențiat, pagina nouă s-a
 * încărcat (reload), întrebăm din nou acolo — SDK-ul nu mai navighează el, doar
 * poartă întrebarea peste reload (sdk.ts → pendingContinuation); `in_page_reveal`
 * = click pe elementul evidențiat, DOM-ul s-a schimbat FĂRĂ reload (accordion,
 * tab, dropdown, rutare SPA). AMBELE schimbă acum promptul (promptBuilder.ts):
 * fără linia lor, modelul ar repeta verbatim răspunsul anterior și ar
 * re-evidenția aceeași țintă, o buclă aparent infinită pentru un vizitator care
 * tocmai a făcut exact ce i s-a cerut.
 *
 * Opțional, ca tot restul câmpurilor de continuare: un SDK vechi din cache-ul
 * browserului nu-l trimite niciodată, iar /ask trebuie să funcționeze identic
 * fără el.
 */
export const continuationReasonSchema = z.enum(["navigate", "in_page_reveal"]);

/**
 * Plafoanele trail-ului de acțiuni — exportate ca valori din același motiv ca
 * plafoanele snapshot-ului: SDK-ul taie ÎNAINTE de trimitere, serverul aplică
 * la validare, un singur set de cifre. 15 = decizie Radu (26 iul 2026): destul
 * cât să acopere drumul până la întrebare, fără să devină jurnal de sesiune.
 */
export const ACTION_TRAIL_MAX_EVENTS = 15;
export const ACTION_TRAIL_MAX_LABEL_CHARS = 80;

/**
 * O acțiune a vizitatorului pe situl clientului, înregistrată de SDK înainte
 * (și între) întrebări, ca modelul să vadă drumul, nu doar ecranul curent.
 *
 * Aceeași promisiune publică ca la snapshot (privacy §2.2, registru → G12):
 * `label` e DOAR text randat (eticheta butonului/linkului/formularului) —
 * NICIODATĂ value dintr-un input. `n` colapsează click-urile identice
 * consecutive („(3 times)" — semnal de frustrare, nu zgomot de 3 rânduri).
 */
export const userActionEventSchema = z.object({
  /** Date.now() la momentul acțiunii, în browserul vizitatorului */
  t: z.number().int().nonnegative(),
  kind: z.enum(["nav", "click", "submit"]),
  path: z.string().max(500),
  label: z.string().max(ACTION_TRAIL_MAX_LABEL_CHARS).optional(),
  n: z.number().int().min(1).max(999).optional(),
});

/**
 * Cum s-a încheiat o sesiune de ghidare (spotlight) pentru vizitator — trimis
 * de SDK ca telemetrie de produs (POST /event), NU ca parte din /ask.
 * - "reached": vizitatorul a ajuns la ținta indicată (click-through confirmat).
 * - "abandoned_dismissed": vizitatorul a închis widgetul înainte de țintă.
 * - "abandoned_expired": ghidarea a expirat (timeout/waitForElement ratat).
 * - "abandoned_new_question": vizitatorul a pus altă întrebare înainte să ajungă.
 * - "target_missing": elementul țintă nu a putut fi găsit pe pagină.
 */
export const guidanceOutcomeSchema = z.enum([
  "reached",
  "abandoned_dismissed",
  "abandoned_expired",
  "abandoned_new_question",
  "target_missing",
]);

/**
 * Corpul lui POST /:appId/event — un singur eveniment de telemetrie de
 * ghidare, fără conținut de conversație (doar rezultatul, pagina și sesiunea).
 */
export const guidanceEventSchema = z.object({
  outcome: guidanceOutcomeSchema,
  path: z.string().max(500),
  targetLabel: z.string().max(ACTION_TRAIL_MAX_LABEL_CHARS).optional(),
  sessionId: z.string().min(1).max(64).optional(),
  t: z.number().int().nonnegative(),
});

/**
 * Evenimentele de SESIUNE ale widgetului — pâlnia dinaintea primei întrebări:
 * `widget_shown` (widgetul chiar s-a montat pe o pagină neexclusă) și
 * `widget_opened` (vizitatorul a deschis bula). Trimise pe același
 * POST /:appId/event ca telemetria de ghidare.
 *
 * `sessionId` e OPȚIONAL aici (deliberat, spre deosebire de o versiune mai
 * veche a acestei scheme): `widget_shown` pleacă ÎNAINTE de orice interacțiune
 * a vizitatorului (montarea widgetului), iar SDK-ul (chatSession.ts) nu are
 * voie să creeze un id de sesiune doar pentru asta — ePrivacy art. 5(3)
 * scutește de consimțământ numai stocarea cerută explicit de utilizator. Când
 * id-ul EXISTĂ deja (tab cu o conversație începută anterior, sau
 * `widget_opened` — care vine mereu după o interacțiune), deduplicarea „o
 * vizită se numără o singură dată" se face SERVER-side pe cheia (appId,
 * sessionId, zi); fără el, evenimentul se înregistrează fără dedupe (vezi
 * recordSessionEvent în backend).
 *
 * FĂRĂ `path`, deliberat: pagina pe care s-a afișat/deschis widgetul nu ne
 * trebuie pentru pâlnie, iar a o colecta ar lărgi datele de vizită dincolo de
 * ce descriu documentele publice (registru → cookies §1 „strictly necessary").
 */
export const sessionEventSchema = z.object({
  // nudge_* (9 aug 2026, docs/plan-commerce-proactivity.md): pâlnia
  // proactivității - arătat / respins / transformat în conversație. Aceleași
  // reguli ca widget_shown: fără path, sessionId doar dacă există deja.
  // voice_resume_* (21 sep 2026, F-07): reluarea vocii după un reload a fost
  // blocată de browser (gest lipsă) sau a picat (sesiune dispărută). Doar
  // pentru diagnoză - se loghează, nu se agregă și nu poartă conținut.
  event: z.enum([
    "widget_shown",
    "widget_opened",
    "nudge_shown",
    "nudge_dismissed",
    "nudge_engaged",
    "voice_resume_blocked",
    "voice_resume_failed",
  ]),
  sessionId: z.string().min(1).max(64).optional(),
  t: z.number().int().nonnegative(),
  /** Doar pe nudge_*: care trigger l-a produs. */
  trigger: z.enum(["hesitation", "stale_cart", "exit_intent"]).optional(),
});

/**
 * Corpul acceptat de POST /:appId/event: forma VECHE de ghidare SAU forma nouă
 * de sesiune. Uniunea nu e cosmetică — browserele servesc sdk.js din cache, deci
 * SDK-uri care nu știu de evenimentele de sesiune vor trimite forma de ghidare
 * mult timp după deploy; ea trebuie să rămână validă neschimbată.
 *
 * Discriminarea se face prin prezența câmpului (`outcome` vs `event`), nu
 * printr-un discriminator comun: adăugarea unuia ar fi rupt exact SDK-urile
 * vechi pe care uniunea le protejează.
 */
export const runtimeEventSchema = z.union([guidanceEventSchema, sessionEventSchema]);

/** Type guard: e un eveniment de sesiune (vs. unul de ghidare)? */
export function isSessionEvent(event: RuntimeEvent): event is SessionEvent {
  return "event" in event;
}

/**
 * Query string-ul pe care SDK-ul îl atașează la GET /config. Singurul scop e
 * traceId-ul cap-coadă (docs/plan-oplog.md → „traceId cap-coadă"): fără
 * sessionId aici, lanțul de log ar începe abia de la prima întrebare, iar
 * bug-urile de inițializare a widgetului (înainte de orice /ask) ar rămâne pe
 * dinafară. Nu se stochează nimic nou — sessionId există deja, generat de SDK
 * pentru /ask (chatSession.ts).
 */
export const configRequestSchema = z.object({
  sessionId: z.string().min(1).max(64).optional(),
});

export const askRequestSchema = z.object({
  question: z.string().min(1).max(1000),
  pageContext: pageContextSchema.optional(),
  // id de sesiune per-tab, generat de widget — leagă întrebările unui vizitator
  // într-o singură conversație în dashboardul clientului (opțional: rândurile
  // vechi n-au avut așa ceva)
  sessionId: z.string().min(1).max(64).optional(),
  // Opționale, și rămân opționale: browserele servesc sdk.js din cache, deci un
  // SDK vechi va trimite cereri fără snapshot mult timp după deploy. Fără
  // snapshot, /ask se comportă exact ca înainte (detaliul paginii din config).
  snapshot: pageSnapshotSchema.optional(),
  // true = un apel automat, fără o nouă întrebare a vizitatorului — după o
  // navigare cerută de agent SAU după un click pe elementul evidențiat care a
  // schimbat pagina (vezi continuationReason); se contorizează (operation_type
  // "ask_continuation") dar NU scade din cota de răspunsuri a clientului.
  continuation: z.boolean().optional(),
  // Conversația a pornit dintr-un NUDGE proactiv (docs/plan-commerce-proactivity.md):
  // ce trigger a deschis-o. Promptul primește contextul („the visitor was nudged
  // because...") ca primul răspuns să continue firul, nu să o ia de la zero.
  nudge: z.enum(["hesitation", "stale_cart", "exit_intent"]).optional(),
  continuationReason: continuationReasonSchema.optional(),
  // Drumul vizitatorului până la această întrebare (vezi userActionEventSchema).
  // Opțional și rămâne opțional, ca snapshot: SDK-urile vechi din cache nu-l
  // trimit, iar /ask trebuie să funcționeze identic fără el.
  recentActions: z.array(userActionEventSchema).max(ACTION_TRAIL_MAX_EVENTS).optional(),
})
  // O continuare fără sessionId n-ar putea fi legată de întrebarea-rădăcină,
  // deci n-ar putea fi plafonată — și „nu scade din cotă" ar deveni un buton de
  // apeluri LLM gratuite pentru oricine ocolește SDK-ul. Structural, nu prin
  // grija apelantului.
  .refine((r) => !r.continuation || r.sessionId, {
    message: "continuation requires sessionId",
    path: ["sessionId"],
  });

export const agentResponseSchema = z.object({
  message: z.string().max(2000),
  actions: z.array(actionSchema).max(10).default([]),
  /**
   * Cota lunară de răspunsuri a CONTULUI e epuizată (peste limita planului, fără
   * bonus și fără credit de overage). Widgetul nu dispare la primirea lui — un
   * vizitator prins la mijlocul unei conversații își termină firul (vezi
   * grațierea din services/answerGrace.ts) — dar de la primul ✕ nu mai revine.
   *
   * Trimis pe /ask, nu doar pe /config, fiindcă /config se cere o singură dată
   * per tab (sdk.ts): un tab deschis ÎNAINTE de epuizare n-ar afla altfel
   * niciodată, iar ✕-ul lui n-ar declanșa dispariția. Costă zero: /ask apelează
   * oricum checkQuota la fiecare cerere, deci valoarea e deja calculată.
   *
   * Default false: un SDK nou care primește răspuns de la un backend vechi
   * (fereastra de după un rollback) se comportă ca azi — widget vizibil.
   */
  answersExhausted: z.boolean().default(false),
});

// poweredBy/branding sunt .optional() cu default — un SDK nou care primește
// un răspuns de la un backend vechi (fereastră scurtă după un rollback, sdk.js
// și backend nu sunt garantat pe aceeași versiune) nu trebuie să arunce ZodError
// și să dispară silențios; defaultul (branding vizibil, fără personalizare) e
// sigur — identic cu ce ar servi un site fără cont.
export const runtimeConfigResponseSchema = z.object({
  config: appConfigSchema,
  /** widget arată linia „Powered by Ask My Website AI"; false doar pt. white-label (Agency+) */
  poweredBy: z.boolean().default(true),
  /** personalizare widget (Growth+); gol pentru Starter, indiferent ce e în DB (downgrade safe) */
  branding: widgetBrandingSchema.default({}),
  /** false până la primul apel real al scriptului de pe situl clientului — vezi SDK sdk.ts + backend appRegistry.markSynced */
  synced: z.boolean().default(false),
  /**
   * Originile permise ale app-ului (apps.allowed_origins, egal cu ce verifică
   * CORS-ul) — folosite de scanul din builder ca să nu iasă niciodată din
   * originile declarate ale clientului. Default gol — un backend vechi fără
   * câmpul ăsta ar lăsa scanul fără scope; builder-ul tratează gol ca „doar
   * originea curentă" (vezi builder/index.ts fetchScanScope).
   */
  allowedOrigins: z.array(z.string()).default([]),
  /**
   * Pattern-uri de URL pe care widgetul NU apare (și nu trimite snapshot):
   * potrivite de SDK pe pathname+hash (vezi sdk/exclusion.ts). Default gol —
   * un backend vechi fără câmpul ăsta = niciun URL exclus, comportamentul de azi.
   */
  excludedUrls: z.array(z.string()).default([]),
  /**
   * Selectoare CSS ale zonelor excluse din snapshot (subtree-ul întreg nu se
   * citește și nu pleacă la LLM) — vezi sdk/snapshot.ts. Default gol, ca mai sus.
   */
  excludedSelectors: z.array(z.string()).default([]),
  /**
   * Cota lunară de răspunsuri a contului e epuizată → widgetul NU se montează
   * pentru un vizitator nou. Excepțiile trăiesc în backend (builder admin, cont
   * de test, app intern fără cont) și în SDK (vizitator cu conversație începută
   * în tabul ăsta, care primește grațierea). Vezi și agentResponseSchema.
   *
   * Calculat, nu stocat: se stinge singur la 1 ale lunii, la upgrade sau la
   * cumpărarea de răspunsuri extra, fără nimic de resetat.
   */
  answersExhausted: z.boolean().default(false),
});

export type PageContext = z.infer<typeof pageContextSchema>;
export type PageSnapshot = z.infer<typeof pageSnapshotSchema>;
export type PageSnapshotElement = z.infer<typeof pageSnapshotElementSchema>;
export type CartState = z.infer<typeof cartStateSchema>;
export type ContinuationReason = z.infer<typeof continuationReasonSchema>;
export type UserActionEvent = z.infer<typeof userActionEventSchema>;
export type GuidanceOutcome = z.infer<typeof guidanceOutcomeSchema>;
export type GuidanceEvent = z.infer<typeof guidanceEventSchema>;
export type SessionEvent = z.infer<typeof sessionEventSchema>;
export type SessionEventName = SessionEvent["event"];
export type RuntimeEvent = z.infer<typeof runtimeEventSchema>;
export type ConfigRequest = z.infer<typeof configRequestSchema>;
export type AskRequest = z.infer<typeof askRequestSchema>;
export type AgentResponse = z.infer<typeof agentResponseSchema>;
export type RuntimeConfigResponse = z.infer<typeof runtimeConfigResponseSchema>;
